import Route from '@ember/routing/route';
import { instrumentRoutePerformance } from '@sentry/ember';

export interface RecommendationItem {
  title: string;
  description: string;
  created_at: string;
  type: 'action' | 'insight' | 'mention';
  icon: string;
}

interface ActionItem extends RecommendationItem {
  reason: string;
  solution: string;
  type: 'action';
  tools_affected: string[];
}

interface InsightItem extends RecommendationItem {
  findings: string;
  recommended_action: string;
  type: 'insight';
}

interface MentionItem extends RecommendationItem {
  reason: string;
  solution: string;
  type: 'mention';
}

class RecommendationsRoute extends Route {
  items: (ActionItem | InsightItem | MentionItem)[] = [
    {
      title: 'Anomaly detected with metric Daily Active Users',
      description:
        'Daily Active Users has dropped 23% below the 7-day average. Investigate the cause of this decline.',
      created_at: '1 hours ago',
      type: 'action',
      reason:
        'Daily Active Users (DAU) has experienced an unusual 23% decline compared to the 7-day moving average. This anomaly was detected by our monitoring system and indicates a potential issue with user engagement, product availability, or external factors affecting user behavior. If left unaddressed, this could signal a larger trend impacting revenue and growth targets.',
      solution:
        'Immediately investigate potential causes: check for system outages or performance issues, review recent product changes or deployments, analyze user feedback and support tickets, and examine external factors like seasonal patterns or competitor activity. Segment the data by user cohort, geography, and platform to identify if specific groups are affected. Once the root cause is identified, create an action plan with the product and engineering teams to address the issue.',
      tools_affected: ['Metrics', 'Goals', 'Google Docs', 'Linear'],
      icon: 'tailwind-icons/arrow-trending-down',
    },
    {
      title: 'User Retention Rate improving across mobile platforms',
      description:
        '7-day retention has increased by 15% on iOS and 12% on Android over the past two weeks.',
      created_at: '3 hours ago',
      type: 'insight',
      findings:
        'Analysis of user cohorts from the last month shows a significant improvement in 7-day retention rates across mobile platforms. iOS retention improved from 42% to 57%, while Android retention increased from 38% to 50%. This positive trend correlates with the recent onboarding flow redesign and push notification optimization implemented three weeks ago.',
      recommended_action:
        'Continue monitoring retention metrics to ensure the trend is sustained. Consider conducting user surveys to identify which specific onboarding changes had the most impact. Document these findings and share best practices with the web team to apply similar improvements to the desktop experience.',
      icon: 'tailwind-icons/arrow-trending-up',
    },
    {
      title: 'API response time degradation on checkout endpoint',
      description:
        'Average response time for /api/checkout has increased from 450ms to 1.2s over the last 48 hours.',
      created_at: '30 minutes ago',
      type: 'action',
      reason:
        'The checkout API endpoint is experiencing significant performance degradation, with average response times increasing by 167%. This is likely causing user frustration and potential revenue loss as customers may abandon purchases during slow checkout processes. The issue began shortly after the database migration to the new cluster and may be related to missing indexes or connection pool configuration.',
      solution:
        'Immediately review database query performance and check for missing indexes on the orders and payments tables. Verify connection pool settings are optimized for the new cluster. Consider implementing caching for frequently accessed checkout data. Roll back the database migration if performance cannot be improved within 2 hours. Set up alerts for API response times exceeding 800ms to catch similar issues faster in the future.',
      tools_affected: ['Metrics', 'Linear', 'Slack'],
      icon: 'tailwind-icons/exclamation-circle',
    },
    {
      title: 'Marketing team mentioned you in goal: Q1 User Acquisition',
      description:
        'Sarah from Marketing tagged you in a comment about the paid ad campaign performance.',
      created_at: '2 hours ago',
      type: 'mention',
      reason:
        "Sarah Chen from the Marketing team has mentioned you in a discussion thread on the Q1 User Acquisition goal. She's requesting technical input on implementing better attribution tracking for the paid advertising campaigns running across Google Ads and Meta platforms.",
      solution:
        "Review Sarah's comment and the current attribution tracking implementation. Schedule a quick sync with the marketing team to understand their tracking requirements. Propose implementing enhanced conversion tracking using server-side events to improve attribution accuracy and prepare a technical specification for the required changes.",
      icon: 'tailwind-icons/bell',
    },
    {
      title:
        'Conversion funnel drop-off at payment step increased significantly',
      description:
        'Payment page abandonment rate jumped from 12% to 28% in the last 3 days.',
      created_at: '45 minutes ago',
      type: 'action',
      reason:
        "There's been a dramatic increase in users abandoning the checkout process at the payment information step. The abandonment rate has more than doubled from the historical average of 12% to 28%. User session recordings show hesitation and multiple form field errors. This coincides with the recent update to the payment form validation logic and may indicate UX issues or technical bugs preventing successful payment submission.",
      solution:
        'Immediately audit the payment form validation changes deployed 3 days ago. Review error logs for payment processing failures and check for any new validation rules that might be too restrictive. Analyze session recordings to identify specific pain points in the user experience. Consider rolling back the validation changes while investigating. Reach out to users who abandoned checkout to understand their experience and offer assistance completing their purchase.',
      tools_affected: ['Metrics', 'Goals', 'Linear', 'Google Docs'],
      icon: 'tailwind-icons/exclamation-circle',
    },
    {
      title: 'Feature adoption exceeding projections for new dashboard',
      description:
        'New analytics dashboard has 67% adoption rate among active users, 22% above target.',
      created_at: '5 hours ago',
      type: 'insight',
      findings:
        'The new analytics dashboard launched two weeks ago has seen exceptional adoption rates. Current data shows 67% of active users have accessed the dashboard at least once, with 45% using it regularly (3+ times per week). This significantly exceeds our initial 45% adoption target. User feedback has been overwhelmingly positive, with an average satisfaction score of 4.6/5. The most used features are the custom date range selector and the export to PDF functionality.',
      recommended_action:
        'Capitalize on this momentum by promoting the dashboard more heavily in user onboarding. Consider creating tutorial content and use case examples to drive even deeper engagement. Gather detailed feedback from power users to inform the roadmap for future dashboard enhancements. Document the launch strategy and adoption tactics for replication with future feature releases.',
      icon: 'tailwind-icons/chart-bar',
    },
  ];

  model() {
    return this.items;
  }
}

export default instrumentRoutePerformance(RecommendationsRoute);
